import { useState, useEffect, useCallback } from 'react';
import {
  getInterestAnalytics,
  getWalkStats,
  getWalkParticipants,
} from '@/lib/api/analytics';
import type { InterestAnalytics, TypedInitiativeStats, TypedParticipant } from '@/types/analytics';

export interface UseAnalyticsReturn {
  typedStats: TypedInitiativeStats | null;
  participants: TypedParticipant[];
  interestData: InterestAnalytics | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

type InitiativeType = string;

function getStatsFetcher(initiativeId: string, initiativeType: InitiativeType) {
  if (initiativeType === 'walk_initiative') {
    return getWalkStats(initiativeId);
  }
  return Promise.resolve(null);
}

function getParticipantsFetcher(initiativeId: string, initiativeType: InitiativeType) {
  if (initiativeType === 'walk_initiative') {
    return getWalkParticipants(initiativeId);
  }
  return Promise.resolve(null);
}

export function useAnalytics(
  initiativeId: string,
  initiativeType: InitiativeType
): UseAnalyticsReturn {
  const [typedStats, setTypedStats] = useState<TypedInitiativeStats | null>(null);
  const [participants, setParticipants] = useState<TypedParticipant[]>([]);
  const [interestData, setInterestData] = useState<InterestAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    if (!initiativeId) return;
    try {
      setLoading(true);
      setError(null);

      const [interestRes, statsRes, participantsRes] = await Promise.allSettled([
        getInterestAnalytics(initiativeId),
        getStatsFetcher(initiativeId, initiativeType),
        getParticipantsFetcher(initiativeId, initiativeType),
      ]);

      if (interestRes.status === 'fulfilled' && interestRes.value?.success && interestRes.value.data) {
        setInterestData(interestRes.value.data);
      }

      if (
        statsRes.status === 'fulfilled' &&
        statsRes.value &&
        'success' in statsRes.value &&
        statsRes.value.success &&
        statsRes.value.data
      ) {
        setTypedStats(statsRes.value.data as TypedInitiativeStats);
      }

      if (
        participantsRes.status === 'fulfilled' &&
        participantsRes.value &&
        'success' in participantsRes.value &&
        participantsRes.value.success &&
        Array.isArray(participantsRes.value.data)
      ) {
        setParticipants(participantsRes.value.data as TypedParticipant[]);
      }
    } catch {
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [initiativeId, initiativeType]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return { typedStats, participants, interestData, loading, error, refetch: fetchAnalytics };
}
